import React, { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

// Shared branding form used by SiteSettings and Sport Branding tabs.
// `fields` prop is an array of field group definitions to render.

export function ColorInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={value || "#0A1128"} onChange={e => onChange(e.target.value)}
          className="h-9 w-12 rounded-sm border border-slate-300 cursor-pointer" data-testid={testid}/>
        <Input value={value || ""} onChange={e => onChange(e.target.value)}
          className="rounded-sm font-mono text-sm h-9" placeholder="#000000"/>
        {value && <button type="button" onClick={() => onChange("")} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4"/></button>}
      </div>
    </div>
  );
}

export function ImageUpload({ label, value, onChange, hint, testid, maxKb = 200 }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const onFile = (f) => {
    if (!f) return;
    if (f.size > maxKb * 1024) { toast.error(`Image must be smaller than ${maxKb}KB`); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => { onChange(reader.result); setUploading(false); toast.success("Image loaded"); };
    reader.onerror = () => { toast.error("Failed to read file"); setUploading(false); };
    reader.readAsDataURL(f);
  };

  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {hint && <p className="text-[10px] text-slate-500 mb-1">{hint}</p>}
      <div className="flex items-center gap-2 mt-1">
        {value && (
          <div className="w-16 h-16 border border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            <img src={value} alt="" className="max-w-full max-h-full object-contain"/>
          </div>
        )}
        <div className="flex-1 flex flex-col gap-1">
          <Input value={value && value.startsWith("data:") ? "(uploaded image)" : (value || "")}
            onChange={e => onChange(e.target.value)}
            readOnly={value && value.startsWith("data:")}
            className="rounded-sm h-8 text-xs" placeholder="Paste URL or upload below"/>
          <div className="flex gap-1">
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={e => onFile(e.target.files?.[0])} data-testid={`${testid}-file`}/>
            <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload-btn`}>
              <Upload className="w-3 h-3 mr-1"/>{uploading ? "..." : "Upload"}
            </Button>
            {value && <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs text-red-600" onClick={() => onChange("")}>Clear</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, testid, type = "text", helpText }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value || ""} onChange={e => onChange(e.target.value)} className="rounded-sm h-9 mt-1" placeholder={placeholder} data-testid={testid}/>
      {helpText && <p className="text-[10px] text-slate-500 mt-0.5">{helpText}</p>}
    </div>
  );
}

export function TextAreaField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Textarea value={value || ""} onChange={e => onChange(e.target.value)} className="rounded-sm mt-1" placeholder={placeholder} data-testid={testid}/>
    </div>
  );
}

export function Section({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-4 py-3 border-b bg-slate-50">
        <h3 className="font-heading text-sm tracking-wide">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}
